"use client";

import React, { useEffect, useRef, useState } from "react";
import { Check, Cpu, MessageSquare, Send, Sparkles, Wrench, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function IndustrialDiagnosticIntelligence() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Turret Example.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Turret Example.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Turret Example.png",
    alt: "Industrial AI Diagnostic Interface - Dashboard"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Haas Vector Drive Example.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Haas Vector Drive Example.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Haas Vector Drive Example.png",
    alt: "Industrial AI Diagnostic Interface - Troubleshooting"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Mazak Hydraulic Pressure Example.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Mazak Hydraulic Pressure Example.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Mazak Hydraulic Pressure Example.png",
    alt: "Industrial AI Diagnostic Interface - Analytics"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Spindle Orientation Adjustment Example.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Spindle Orientation Adjustment Example.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot - Doosan Spindle Orientation Adjustment Example.png",
    alt: "Industrial AI Diagnostic Interface - Parameter Optimization"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Hwacheon Spindle Stop Example.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Hwacheon Spindle Stop Example.png",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577400/img/Screenshot Hwacheon Spindle Stop Example.png",
    alt: "Industrial AI Diagnostic Interface - Maintenance Scheduling"
  }];
  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };
  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const benefits = ["Instant resolutions for complex issues", "Interactive, step-by-step procedures", "Trained on 45+ years of service knowledge", "Reduce costly service calls"];
  return <section id="industrial-diagnostic-intelligence" className="relative w-full bg-background py-24 lg:py-32 overflow-hidden" ref={sectionRef}>
      
      <div className="absolute inset-0 pointer-events-none opacity-[0.04] dark:opacity-[0.08] mix-blend-multiply dark:mix-blend-screen transition-opacity duration-1000" style={{
      backgroundImage: "url(/img/generic_haas_milling_machine.png)",
      backgroundSize: "cover",
      backgroundPosition: "center"
    }} />

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        <div className="flex flex-col gap-12 lg:gap-16 items-center text-center">
          
          <div ref={rightRef} className={`transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6 leading-[1.1]">
              Stop waiting for <br className="hidden lg:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-destructive">
                $200/hr service.
              </span>
            </h2>
          </div>

          <div className={`flex flex-col items-center transition-all duration-1000 delay-300 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-3xl">The CNCWise Ai Technician acts as your in-house expert, providing instant, accurate troubleshooting guidance to get you back to production quickly. See a few examples of the CNCWise Ai Technician below.</p>

            <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-4 mb-10 text-left">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 bg-primary/10 rounded-lg p-1 shrink-0">
                    <Check className="size-5 text-primary" strokeWidth={3} />
                  </div>
                  <span className="text-foreground font-medium text-lg">{benefit}</span>
                </li>)}
            </ul>

          </div>

          <div ref={leftRef} className={`relative w-full max-w-5xl transition-all duration-1000 delay-200 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            
            <div className="absolute -inset-4 bg-primary/20 blur-[80px] rounded-lg opacity-50 -z-10" />

            <div className="group relative rounded-2xl overflow-hidden border-x border-y-4 border-primary/10 bg-card/30 backdrop-blur-sm shadow-2xl">
              <div className="relative overflow-hidden">
                {images.map((image, idx) => <img key={idx} data-index={idx} src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" className={`w-full h-auto object-cover transition-opacity duration-500 ease-in-out ${idx === currentImageIndex ? "relative opacity-100 z-10" : "absolute inset-0 opacity-0 z-0"}`} />)}
              </div>
            </div>

            {images.length > 1 && <div className="relative flex items-center justify-between mt-4 pointer-events-none">
                <Button variant="outline" size="icon" onClick={prevImage} className="rounded-lg bg-background/80 backdrop-blur-md border-primary/20 hover:bg-primary hover:text-primary-foreground pointer-events-auto shadow-md">
                  <ChevronLeft className="size-6" />
                </Button>
                <div className="flex justify-center gap-2 px-3 py-2 rounded-lg pointer-events-auto">
                  {images.map((_, idx) => <Button key={idx} data-index={idx} variant="ghost" size="icon" onClick={() => setCurrentImageIndex(idx)} className={`size-2.5 min-w-0 p-0 rounded-lg transition-all duration-300 hover:bg-primary/50 ${idx === currentImageIndex ? "bg-primary w-6" : "bg-primary/30"}`} aria-label={`Go to slide ${idx + 1}`} />)}
                </div>
                <Button variant="outline" size="icon" onClick={nextImage} className="rounded-lg bg-background/80 backdrop-blur-md border-primary/20 hover:bg-primary hover:text-primary-foreground pointer-events-auto shadow-md">
                  <ChevronRight className="size-6" />
                </Button>
              </div>}

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Button size="lg" className="text-base h-14 px-8 shadow-lg shadow-primary/20" asChild>
                <Link to="https://app.cncwise.com/signup">
                  Start Troubleshooting Now
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}